import { useMemo } from 'react';
import { getIDFromUrl } from '@rossum/api-client';
import { useListRules } from '../../rules/hooks/useListRules';

/**
 * Hook that fetches rules only for the specified queue IDs and builds
 * a map of queue ID to rule count.
 *
 * This is an optimization over fetching all rules - when queue filters
 * are applied, we only fetch rules for the visible queues.
 */
export const useRulesCountMap = (queueIds: number[]) => {
  const { data: rulesData, isLoading } = useListRules(
    // Pass queue IDs to the API to fetch only relevant rules
    // When queueIds is empty, we still need to fetch to show 0 counts
    { queue: queueIds.length > 0 ? queueIds : undefined },
    { enabled: queueIds.length > 0 }
  );

  const rulesCountMap = useMemo(() => {
    const map = new Map<number, number>();

    // Initialize all queue IDs with 0 count
    queueIds.forEach(queueId => {
      map.set(queueId, 0);
    });

    // Count rules per queue
    rulesData?.forEach(rule => {
      rule.queues.forEach(queueUrl => {
        const queueId = getIDFromUrl(queueUrl);
        // Only count if this queue is in our visible list
        if (map.has(queueId)) {
          const currentCount = map.get(queueId) ?? 0;
          map.set(queueId, currentCount + 1);
        }
      });
    });

    return map;
  }, [rulesData, queueIds]);

  return { rulesCountMap, isLoading };
};
